import {
    AfterViewInit,
    Component,
    ElementRef,
    OnInit,
    QueryList,
    ViewChild,
    ViewChildren,
    ViewEncapsulation
} from '@angular/core';
import {Router} from "@angular/router";
import {FormBuilder, FormControl, FormGroup, Validators} from "@angular/forms";
import {DtcModuleService, DtcModuleSubmitResponse} from "@portal/api/services/dtc-module-service";
import {StorageService} from "@portal/shared/services/storage.service";
import {SearchModel} from "@portal/shared/models/search-model";
import {catchError, of} from "rxjs";
import {COMMA, ENTER} from "@angular/cdk/keycodes";
import {MatChipInputEvent} from "@angular/material/chips";
import {SignatureData} from "@portal/api/responses/dtc-removal/signature-data";
import {MapType} from "@portal/api/enums/map-type";
import {MatDialog} from "@angular/material/dialog";
import {DeleteDialogComponent} from "@portal/shared/delete-dialog/delete-dialog.component";
import {EnumNamesService} from "@portal/shared/services/enum-names.service";
import {reflectTypeEntityToDeclaration} from "@angular/compiler-cli/src/ngtsc/reflection";
import {SignatureDataMap} from "@portal/api/responses/dtc-removal/signature-data-map";
import { ModificationDataType } from '@portal/api/enums/modification-data-type';
import chroma from "chroma-js";
import {DtcBySystemService} from "@portal/api/services/dtc-by-system.service";
import {DtcBySystemResponse} from "@portal/api/sections/dtc-by-system/dtc-by-system-response";
import {Guid} from "guid-typescript";

@Component({
    selector: 'app-dtc-removal',
    templateUrl: './dtc-removal.component.html',
    styleUrls: ['./dtc-removal.component.scss'],
    encapsulation: ViewEncapsulation.None
})
export class DtcRemovalComponent implements OnInit, AfterViewInit {
    data: any;
    dataName: SearchModel;
    form: FormGroup;
    formErrors: FormGroup;
    filePassed: boolean;
    @ViewChild('fileInput') fileInput: ElementRef;
    fileName: string = '';
    isLoadingSpinnerActive: boolean;
    errors: string[] = [];
    manualErrors: Set<string> = new Set();
    dtcErrors: Set<string> = new Set();
    selectedDtcProfiles: { id: string, name: string, errors: string }[] = [];
    signatureData: SignatureData;
    readonly separatorKeysCodes = [ENTER, COMMA] as const;
    @ViewChildren('mapContainer') mapContainers: QueryList<ElementRef<HTMLDivElement>>;
    private scrollMapping = new Map<EventTarget, ElementRef<HTMLDivElement>>();
    lastErrorClicked: string;
    lastErrorIndexScrolled: number;
    lastErrorIndexScrolledManufacturer: number | null = null;
    lastErrorIndexScrolledDescriptions: number | null = null;
    currentTable: 'manufacturer' | 'descriptions' = 'manufacturer';

    constructor(
        public dialog: MatDialog,
        private _router: Router,
        protected fb: FormBuilder,
        private _enumService: EnumNamesService,
        private _dtcModuleService: DtcModuleService,
        private _dtcBySystemService: DtcBySystemService,
        private _storageService: StorageService) {
    }

    ngAfterViewInit(): void {
        this.mapContainers.forEach((container) => {
            this.scrollMapping.set(container.nativeElement, container);
        });
    }

    ngOnInit(): void {
        this.filePassed = false;
        this.data = this._storageService.getCurrentSearchData();
        this.dataName = this._storageService.getCurrentSearchNameData();
        if (this.data === null) {
            this._router.navigate(['/']);
        }
        this.form = this.fb.group({
            file: new FormControl(null, [Validators.required]),
        });
        this.formErrors = this.fb.group({
            errors: [null, [Validators.required]],
            dtcProfiles: [null],
        });

        this._dtcBySystemService.getAll().subscribe(dtcList => {
            this.dtcBySystemList = dtcList.data;
        });

    }

    dtcBySystemList: DtcBySystemResponse[] = []= [];
    selectedDtcIds: DtcBySystemResponse[] = [] = [];

    onDtcMultiSelect(selected: { id: string, name: string, errors: string }[]): void {
        const newDtcErrors = new Set<string>();

        selected.forEach(dtc => {
            dtc.errors.split(',').forEach(e => {
                const trimmed = e.trim().toUpperCase();
                if (trimmed) newDtcErrors.add(trimmed);
            });
        });

        this.dtcErrors = newDtcErrors;

        // Combina os dois
        this.errors = Array.from(new Set([...this.manualErrors, ...this.dtcErrors]));
        this.formErrors.get('errors')?.setValue(this.errors.join(','));

        // Guardar os profiles selecionados
        this.selectedDtcProfiles = selected;

        // Update selectedDtcIds with the corresponding DtcBySystemResponse objects
        this.selectedDtcIds = selected.map(selectedDtc => {
            return this.dtcBySystemList.find(dtc => dtc.id.toString() === selectedDtc.id) || null;
        }).filter(dtc => dtc !== null) as DtcBySystemResponse[];

        // Atualizar visual nos mapas
        const allErrors = this.errors;
        [MapType.DtcDescriptions, MapType.DtcDescriptionsManufacturer].forEach(type => {
            const table = this.signatureData.maps.find(x => x.mapType === type);
            table?.hexValues.forEach(h => {
                h.isSelect = allErrors.includes(h.value);
            });
        });
    }



    onFileSelected(event: Event): void {
        const file = (event.target as HTMLInputElement).files[0];
        if (file) {
            this.fileName = file.name;  // Update the UI to show the file name
            this.form.get('file').setValue(file);
        }
    }

    triggerFileInput(): void {
        this.fileInput.nativeElement.click();  // Trigger the file input dialog
    }
    removeError(error: string): void {
        this.manualErrors.delete(error);
        this.dtcErrors.delete(error);

        this.errors = Array.from(new Set([...this.manualErrors, ...this.dtcErrors]));
        this.formErrors.get('errors')?.setValue(this.errors.join(','));

        // Desmarcar visualmente
        [MapType.DtcDescriptions, MapType.DtcDescriptionsManufacturer].forEach(type => {
            const table = this.signatureData.maps.find(x => x.mapType === type);
            table?.hexValues.forEach(h => {
                if (h.value === error) {
                    h.isSelect = false;
                    h.isScrolled = false;
                }
            });
        });
    }

    onErrorClick(error: string): void {
        if (this.lastErrorClicked !== error) {
            this.lastErrorIndexScrolledManufacturer = null;
            this.lastErrorIndexScrolledDescriptions = null;
            this.currentTable = 'manufacturer';
        }

        const manufacturerDescriptionsTable = this.signatureData.maps.find(
            (x) => x.mapType === MapType.DtcDescriptionsManufacturer
        );
        const descriptionsTable = this.signatureData.maps.find(
            (x) => x.mapType === MapType.DtcDescriptions
        );

        const manufacturerHexValues = manufacturerDescriptionsTable?.hexValues || [];
        const descriptionsHexValues = descriptionsTable?.hexValues || [];

        let foundIndex = -1;

        // Procurar na tabela atual
        if (this.currentTable === 'manufacturer') {
            foundIndex = manufacturerHexValues.findIndex((element, i) => {
                if (
                    element.value === error &&
                    (this.lastErrorIndexScrolledManufacturer === null ||
                        i > this.lastErrorIndexScrolledManufacturer)
                ) {
                    return true;
                }
                return false;
            });

            if (foundIndex !== -1) {
                // Encontrado em DtcDescriptionsManufacturer
                this.lastErrorIndexScrolledManufacturer = foundIndex;
            } else {
                // Não há mais ocorrências em Manufacturer, mudar para Descriptions
                this.currentTable = 'descriptions';
                this.lastErrorIndexScrolledDescriptions = null;
            }
        }

        if (this.currentTable === 'descriptions') {
            foundIndex = descriptionsHexValues.findIndex((element, i) => {
                if (
                    element.value === error &&
                    (this.lastErrorIndexScrolledDescriptions === null ||
                        i > this.lastErrorIndexScrolledDescriptions)
                ) {
                    return true;
                }
                return false;
            });

            if (foundIndex !== -1) {
                // Encontrado em DtcDescriptions
                this.lastErrorIndexScrolledDescriptions = foundIndex;
            } else {
                // Não há mais ocorrências em Descriptions, voltar para o primeiro em Manufacturer
                this.currentTable = 'manufacturer';
                this.lastErrorIndexScrolledManufacturer = null;

                foundIndex = manufacturerHexValues.findIndex(
                    (element) => element.value === error
                );

                if (foundIndex !== -1) {
                    this.lastErrorIndexScrolledManufacturer = foundIndex;
                } else {
                    // Erro não encontrado em nenhuma tabela
                    return;
                }
            }
        }

        // Agora, fazer o scroll até foundIndex na tabela atual
        const currentMapType =
            this.currentTable === 'manufacturer'
                ? MapType.DtcDescriptionsManufacturer
                : MapType.DtcDescriptions;

        // Encontrar o container correspondente ao MapType atual
        const containerRef = this.mapContainers.find((container) => {
            const mapTypeAttribute = container.nativeElement.getAttribute(
                'data-map-type'
            );
            return mapTypeAttribute === currentMapType.toString();
        });

        if (!containerRef) {
            console.error('Contêiner não encontrado para MapType:', currentMapType);
            return;
        }

        const container = containerRef.nativeElement;

        // Acessar o elemento alvo corretamente
        const hexValuesContainer = container.querySelector('.hex-values');
        if (!hexValuesContainer) {
            console.error('hex-values não encontrado no contêiner.');
            return;
        }

        const targetElement = hexValuesContainer.children[foundIndex] as HTMLElement;
        if (!targetElement) {
            console.error('Elemento alvo não encontrado para o índice:', foundIndex);
            return;
        }

        // Realizar o scroll
        const containerWidth = container.clientWidth;
        const elementWidth = targetElement.clientWidth;
        const elementLeft = targetElement.offsetLeft;
        const elementCenter = elementLeft + elementWidth / 2;
        const scrollLeft = Math.max(0, elementCenter - containerWidth / 2);
        container.scrollLeft = scrollLeft;

        // Atualizar isScrolled
        if (this.currentTable === 'manufacturer') {
            manufacturerHexValues.forEach((element, i) => {
                element.isScrolled = i === this.lastErrorIndexScrolledManufacturer;
            });
            descriptionsHexValues.forEach((element) => (element.isScrolled = false));
        } else {
            descriptionsHexValues.forEach((element, i) => {
                element.isScrolled = i === this.lastErrorIndexScrolledDescriptions;
            });
            manufacturerHexValues.forEach((element) => (element.isScrolled = false));
        }

        this.lastErrorClicked = error;
    }

    addError(event: MatChipInputEvent): void {
        const inputValue = (event.value || '').trim().toUpperCase();

        // Split the input by spaces to handle multiple errors
        const valuesToAdd = inputValue.split(' ').filter(value => value.trim() !== '');

        const descriptionsTable = this.signatureData.maps.find(x => x.mapType === MapType.DtcDescriptions);
        const manufacturerDescriptionsTable = this.signatureData.maps.find(x => x.mapType === MapType.DtcDescriptionsManufacturer);

        const descriptions = descriptionsTable ? descriptionsTable.hex.split(' ') : [];
        const manufacturerDescriptions = manufacturerDescriptionsTable ? manufacturerDescriptionsTable.hex.split(' ') : [];

        // Array to collect errors that weren't found
        const notFoundErrors: string[] = [];

        // Process each value
        valuesToAdd.forEach(value => {
            const upperValue = value.toUpperCase();

            // Skip if already exists in errors
            if (this.errors.includes(upperValue)) {
                // Show alert only if it's from DTC errors (not manual)
                if (this.dtcErrors.has(upperValue)) {
                    this.dialog.open(DeleteDialogComponent, {
                        data: {
                            title: `Error ${upperValue} is already applied from DTC Profile`,
                            hasOkButton: true
                        }
                    });
                }
                // If it's manual error, ignore silently
                return;
            }

            let found = false;

            // Check if the value exists in either table
            if (descriptions.includes(upperValue) || manufacturerDescriptions.includes(upperValue)) {
                found = true;
            }

            if (!found) {
                notFoundErrors.push(upperValue);
            } else {
                // Add to manualErrors Set
                this.manualErrors.add(upperValue);

                // Set as selected in descriptions table
                descriptionsTable?.hexValues.forEach(element => {
                    if (element.value === upperValue) {
                        element.isSelect = true;
                    }
                });

                // Set as selected in manufacturer descriptions table
                manufacturerDescriptionsTable?.hexValues.forEach(element => {
                    if (element.value === upperValue) {
                        element.isSelect = true;
                    }
                });
            }
        });

        // Update errors array combining manualErrors and dtcErrors
        this.errors = Array.from(new Set([...this.manualErrors, ...this.dtcErrors]));

        // Update the form control with the new errors
        this.formErrors.get('errors')?.setValue(this.errors.join(','));

        // Show dialog for errors that weren't found
        if (notFoundErrors.length > 0) {
            this.dialog.open(DeleteDialogComponent, {
                data: {
                    title: `Error${notFoundErrors.length > 1 ? 's' : ''} not found: ${notFoundErrors.join(', ')}`,
                    hasOkButton: true
                }
            });
        }

        // Clear the input value
        event.chipInput!.clear();
    }


    getMapTypeName(mapType: MapType): string {
        return this._enumService.getMapTypeName(mapType);
    }

    syncScroll(event: Event): void {
        const source = event.target as HTMLDivElement;
        const {scrollLeft} = source;

        this.mapContainers.forEach((container) => {
            const target = container.nativeElement;
            if (target !== source) {
                target.scrollLeft = scrollLeft;
            }
        });
    }

    save() {
        if (this.filePassed) {
            if (this.formErrors.valid) {
                const formData = new FormData();
                formData.append('errors', this.formErrors.get('errors').value);
                formData.append('brandId', this.data.brandId);
                formData.append('ecuProducerId', this.data.ecuProducerId);
                formData.append('ecuTypeId', this.data.ecuTypeId);
                formData.append('file', this.form.get('file').value);
                
                // Add selected DTC by system IDs
                if (this.selectedDtcIds && this.selectedDtcIds.length > 0) {
                    this.selectedDtcIds.forEach((dtc, index) => {
                        formData.append(`dtcBySystemIds[${index}]`, dtc.id.toString());
                    });
                }

                // submit and download the file
                this.isLoadingSpinnerActive = true;

                this._dtcModuleService.submit(formData).pipe(
                    // Handle errors
                    catchError((error: any) => {
                        this.isLoadingSpinnerActive = false;

                        // Log error
                        console.error('Submission error:', error);

                        // Handle specific error codes (e.g., 400, 500, etc.)
                        if (error.status === 400) {
                            // add a dialog with error message
                            alert('Invalid data.');
                        } else if (error.status === 415) {
                            alert('Unsupported Media Type: Please upload a valid file format.');
                        } else {
                            alert('An error occurred while submitting the form. Please try again.');
                        }

                        // Return an empty observable to stop further processing
                        return of(null);
                    })
                ).subscribe((response) => {
                    this.isLoadingSpinnerActive = false;

                    if (response.type === 4) {
                        // The response body contains the patch information
                        const responseBody = response.body;
                        
                        // Check if response body has patch information (JSON response)
                        if (responseBody && typeof responseBody === 'object' && 'appliedPatches' in responseBody) {
                            const jsonResponse = responseBody as DtcModuleSubmitResponse;
                            const appliedPatches = jsonResponse.appliedPatches || [];
                            const totalPatchesApplied = jsonResponse.totalPatchesApplied || 0;
                            
                            console.log('JSON Response received:', jsonResponse);
                            console.log('File bytes type:', typeof jsonResponse.fileBytes);
                            const fileBytes = jsonResponse.fileBytes as any;
                            if (Array.isArray(fileBytes)) {
                                console.log('File bytes length (array):', fileBytes.length);
                            } else if (typeof fileBytes === 'string') {
                                console.log('File bytes length (string):', fileBytes.length);
                            } else if (fileBytes instanceof ArrayBuffer) {
                                console.log('File bytes length (ArrayBuffer):', fileBytes.byteLength);
                            } else if (fileBytes instanceof Blob) {
                                console.log('File bytes size (Blob):', fileBytes.size);
                            } else if (fileBytes instanceof Uint8Array) {
                                console.log('File bytes length (Uint8Array):', fileBytes.length);
                            }
                            
                            // Show patch information dialog if patches were applied
                            if (totalPatchesApplied > 0) {
                                const patchNamesList = appliedPatches.map(patch => `• ${patch}`).join('<br>');
                                const dialogContent = `
                                    <div class="text-2xl font-bold mb-4">DTC Patches Applied Successfully!</div>
                                    <div class="mb-4">
                                        <strong>Patches Applied:</strong> ${totalPatchesApplied}
                                    </div>
                                    <div class="mb-4">
                                        <strong>Applied Patches:</strong><br>
                                        ${patchNamesList}
                                    </div>
                                `;

                                const dialogRef = this.dialog.open(DeleteDialogComponent, {
                                    data: {
                                        title: dialogContent,
                                        hasOkButton: true
                                    }
                                });

                                dialogRef.afterClosed().subscribe(() => {
                                    this.downloadFile(jsonResponse.fileBytes);
                                });
                            } else {
                                // No patches applied, download file directly
                                this.downloadFile(jsonResponse.fileBytes);
                            }
                        } else {
                            // Response is a blob (file download), no patches applied
                            console.log('Blob response received:', responseBody);
                            this.downloadFile(response.body as Blob);
                        }
                    } else {
                        // Response type is not 4, download file directly
                        this.downloadFile(response.body as Blob);
                    }
                });
            }
        } else {
            if (this.form.valid) {
                const formData = new FormData();
                formData.append('brandId', this.data.brandId);
                formData.append('ecuProducerId', this.data.ecuProducerId);
                formData.append('ecuTypeId', this.data.ecuTypeId);
                formData.append('file', this.form.get('file').value);

                // submit and download the file
                this.isLoadingSpinnerActive = true;

                this._dtcModuleService.checkFile(formData).pipe(
                    // Handle errors
                    catchError((error: any) => {
                        this.isLoadingSpinnerActive = false;

                        // Handle specific error codes (e.g., 400, 500, etc.)
                        if (error.status === 400) {
                            // add a dialog with error message
                            if (error.error.message === 'No solutions for that file') {

                                const dialogRef = this.dialog.open(DeleteDialogComponent, {
                                    data: {
                                        title: `
                                          <div class="text-2xl font-bold  mb-2">New ECU Software Variation Detected!</div>
                                          <div class="mb-2">
                                            The loaded file corresponds to a
                                            <strong>supported ECU model</strong>,
                                            but this specific software version is not yet in our database.
                                          </div>
                                          <div class="text-2xl font-bold  mb-2 mt-4">
                                            Action Required:
                                          </div>
                                          <div>
                                            To enable <u>full functionality</u>, please submit this file to our support team by clicking "Yes".
                                            Typical resolution time is under 15 minutes.
                                          </div>
                                        `,
                                        hasYesButton: true,
                                        hasNoButton: true,
                                    }
                                });

                                dialogRef.afterClosed().subscribe(result => {
                                    if (result === 'yes') {
                                        // send the file to support via api
                                        this.isLoadingSpinnerActive = true;

                                        this._dtcModuleService.sendToSupport(formData).pipe(
                                            catchError((error: any) => {
                                                this.isLoadingSpinnerActive = false;
                                                console.error('Submission error:', error);
                                                return of(null);
                                            })
                                        ).subscribe((response) => {
                                            this.isLoadingSpinnerActive = false;
                                            if (response.type === 4) {
                                                this.dialog.open(DeleteDialogComponent, {
                                                    data: {
                                                        title: 'The file has been sent to support',
                                                        hasOkButton: true
                                                    }
                                                });
                                                // Redirect to the home page
                                                this._storageService.clearCurrentSearchData();
                                                this._router.navigate(['/']);
                                            }
                                        });
                                    }
                                });
                            } else {
                                this.dialog.open(DeleteDialogComponent, {
                                    data: {
                                        title: 'Invalid data',
                                        hasOkButton: true
                                    }
                                });
                            }
                        } else if (error.status === 415) {
                            this.dialog.open(DeleteDialogComponent, {
                                data: {
                                    title: 'Unsupported Media Type: Please upload a valid file format',
                                    hasOkButton: true
                                }
                            });
                        } else {
                            this.dialog.open(DeleteDialogComponent, {
                                data: {
                                    title: 'An error occurred while submitting the form. Please try again',
                                    hasOkButton: true
                                }
                            });
                        }

                        // Return an empty observable to stop further processing
                        return of(null);
                    })
                ).subscribe((response) => {
                    // process the response events here
                    if (response.type === 4) {
                        this.filePassed = true;
                        this.isLoadingSpinnerActive = false;
                        this.signatureData = response.body;

                        const customOrder = [
                            MapType.DtcDescriptionsManufacturer,
                            MapType.DtcDescriptions,
                            MapType.DtcClasses,
                            MapType.DtcMasks
                        ];
                        this.signatureData.maps = this.signatureData.maps.sort((a, b) => {
                            return customOrder.indexOf(a.mapType) - customOrder.indexOf(b.mapType);
                        });

                        this.signatureData.maps.forEach(element => {
                            element.hexValues = element.hex.split(' ').map((value) => {
                                return {value, isSelect: false, isScrolled: false};
                            });
                        });
                    }
                });
            }
        }
    }

    getColorByMap(map: SignatureDataMap, value: string): string {

        const allowedEmails = ['pklides@gmail.com', 'vc@vilauto.com'];
        if (!allowedEmails.includes(this._storageService.getCurrentUser().email)) {
            return 'transparent';
        }

        const parsed = map.isDecimal ? parseInt(value, 10) : parseInt(value, 16);
        if (isNaN(parsed)) return 'gray';

        let max: number;
        switch (map.modificationDataType) {
            case ModificationDataType.BIT8: max = 255; break;
            case ModificationDataType.BIT16LoHi:
            case ModificationDataType.BIT16HiLo: max = 65535; break;
            case ModificationDataType.BIT32LoHi:
            case ModificationDataType.BIT32HiLo:
            case ModificationDataType.FLOATLoHi:
            case ModificationDataType.FLOATHiLo: max = 4294967295; break;
            default: return 'gray';
        }

        const percentage = Math.min(parsed / max, 1);

        const scale = chroma.scale(['#0000ff', '#8a00d4', '#ff0080', '#ff0000']); // Azul → Roxo → Rosa → Vermelho
        return scale(percentage).hex();
    }

    downloadFile(fileBody: any): void {
        // Remove extension from original filename
        const fileNameWithoutExtension = this.fileName.replace(/\.[^/.]+$/, '');

        // Create filename suffix
        const profileNames = this.selectedDtcProfiles.map(profile => profile.name).join('_');
        const manualErrorsArray = Array.from(this.manualErrors);
        const manualErrorsSuffix = manualErrorsArray.length > 0 ? manualErrorsArray.join('_') : '';

        // Build filename parts
        let filenameParts = [fileNameWithoutExtension];
        if (profileNames) filenameParts.push(profileNames);
        if (manualErrorsSuffix) filenameParts.push(manualErrorsSuffix);

        const downloadFileName = `${filenameParts.join('_')}.bin`;

        let blob: Blob;

        // Handle different response formats
        if (fileBody instanceof Blob) {
            // Direct blob response
            blob = fileBody;
        } else if (Array.isArray(fileBody)) {
            // C# byte array becomes number[] in JSON - convert to Uint8Array
            const uint8Array = new Uint8Array(fileBody);
            blob = new Blob([uint8Array], {type: 'application/octet-stream'});
        } else if (fileBody instanceof Uint8Array) {
            // Uint8Array - convert to blob
            blob = new Blob([fileBody], {type: 'application/octet-stream'});
        } else if (typeof fileBody === 'string') {
            // Base64 string - convert to blob
            const byteCharacters = atob(fileBody);
            const byteNumbers = new Array(byteCharacters.length);
            for (let i = 0; i < byteCharacters.length; i++) {
                byteNumbers[i] = byteCharacters.charCodeAt(i);
            }
            const byteArray = new Uint8Array(byteNumbers);
            blob = new Blob([byteArray], {type: 'application/octet-stream'});
        } else if (fileBody instanceof ArrayBuffer) {
            // ArrayBuffer - convert to blob
            blob = new Blob([fileBody], {type: 'application/octet-stream'});
        } else if (fileBody && typeof fileBody === 'object' && fileBody.byteLength !== undefined) {
            // TypedArray or similar - convert to blob
            blob = new Blob([fileBody], {type: 'application/octet-stream'});
        } else {
            // Fallback - try to create blob as is
            console.warn('Unknown file body format:', typeof fileBody, fileBody);
            blob = new Blob([fileBody], {type: 'application/octet-stream'});
        }

        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = downloadFileName;
        a.click();
        window.URL.revokeObjectURL(url);
        this._storageService.clearCurrentSearchData();
        this._router.navigate(['/']);
    }

    getColorStyle(map: SignatureDataMap, value: string): { [klass: string]: any } | null {
        const allowedEmails = ['pklides@gmail.com', 'vc@vilauto.com'];
        if (!allowedEmails.includes(this._storageService.getCurrentUser().email)) {
            return null;
        }

        return {
            'background-color': this.getColorByMap(map, value),
            'color': 'white',
        };
    }

}
